import loadable from '@loadable/component';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React from 'react';
import Client from 'shopify-buy';
import tagProducts from '../queries/tagProducts';
import ActionButton from './ActionButton';
import AudioPlayers from './AudioPlayer';
import ConservationBanner from './ConservationBanner';
import If from './If';
import Installers from './Installers';
import PluginProductDesc, {
  PluginPrice,
  PluginProductName,
} from './PluginProductDesc';
import PluginProductGallery from './PluginProductGallery';
import ProductAccordion from './ProductAccordion';
import QuoteCarousel from './QuoteCarousel';
import SectionHeader from './SectionHeader';
import SimpleCollection from './SimpleCollection';
import { TypeOcr } from './Types';

const VideoGallery = loadable(() => import('./VideoGallery'));

function BundleAndSave() {
  return (
    <Box
      sx={{
        backgroundColor: 'secondary.main',

        width: '98%',
        height: { xs: '10rem', sm: '8rem', md: '6rem', lg: '6rem' },

        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Stack direction='row'>
        <TypeOcr sx={{ fontSize: 14, ml: 1, mr: 1 }} align='center'>
          BUILD YOUR OWN BUNDLE: Buy two individual plugins, get a 3rd half off!
        </TypeOcr>
      </Stack>
    </Box>
  );
}

export default function PluginProductPage(props: any) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('lg'));

  const bundle = props.bundleAndSave === 'true';

  const tags = props.tags.filter(
    (t: string) => t !== 'New' && !t.endsWith('hp')
  );
  const alsoLike = tagProducts(
    tags,
    ['Legacy'],
    tags,
    props.handle,
    3,
    0,
    false
  );
  const [variant, setVariantState] = React.useState(0);
  const variantImages = props.variants.nodes.map((v: any) => v.image);
  const [index, setIndexState] = React.useState(0);
  const setIndex = (i: number) => {
    setIndexState(i);
  };
  const setVariant = (v: number) => {
    setVariantState(v);
    const variantImage: Client.Image = variantImages[v];
    const i = props.images.map((i: any) => i.url).indexOf(variantImage.src);
    if (i >= 0) {
      setIndex(i);
    }
  };
  const manual =
    props.productType !== 'Sample Pack' &&
    props.productType !== 'Reason' &&
    props.productType !== 'Apparel' &&
    props.productType !== 'Swag' &&
    props.productType !== 'Panel';

  return (
    <>
      <If value={!isSmall}>
        <Stack
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <PluginProductGallery
            images={props.images}
            index={index}
            setIndex={setIndex}
          ></PluginProductGallery>
        </Stack>
        <Divider
          sx={{ mt: 1 }}
          flexItem
          color='black'
          role='presentation'
        ></Divider>
        <PluginProductName title={props.title}></PluginProductName>
        <PluginPrice variant={variant} {...props} />
        <Stack
          sx={{ justifyContent: 'center' }}
          direction={{ xs: 'column', sm: 'column', md: 'column', lg: 'row' }}
        >
          <PluginProductDesc
            variant={variant}
            setVariant={setVariant}
            {...props}
          ></PluginProductDesc>
          <Stack width={{ sm: '98%', md: '100%' }} mt={1}>
            <ActionButton
              productType={props.productType}
              learnMore={props.learnMore}
              id={props.variants.nodes[0].id}
              available={(props.variants.nodes[0] as any).availableForSale}
              name={props.title}
              isLegacy={props.tags.includes('Legacy')}
            />
            <If value={manual}>
              <Button
                variant='outlined'
                style={{
                  borderRadius: '0px',
                  width: '98%',
                  fontFamily: 'OCR A Std',
                }}
                sx={{ mt: 2, mb: 2 }}
                color='info'
                href={props.manual_link}
              >
                Product Manual
              </Button>
            </If>
            <If value={bundle}>
              <BundleAndSave />
            </If>
            <Installers></Installers>
            <ProductAccordion {...props}></ProductAccordion>
          </Stack>
        </Stack>

        <Divider
          sx={{ mt: 1 }}
          flexItem
          color='black'
          role='presentation'
        ></Divider>

        <If value={props.videos}>
          <VideoGallery {...props} />
        </If>
        <If value={props.sounds}>
          <Divider
            sx={{ mt: 1 }}
            flexItem
            color='black'
            role='presentation'
          ></Divider>
          <AudioPlayers files={props.sounds}></AudioPlayers>
        </If>
        <If value={alsoLike.length !== 0}>
          <SectionHeader
            title='you might also like'
            buttonText='View All'
            href='/collections/all'
          ></SectionHeader>
          <SimpleCollection products={alsoLike} />
        </If>
        <If value={props.quotes}>
          <QuoteCarousel items={props.quotes}></QuoteCarousel>
        </If>
        <ConservationBanner
          conservation={props.conservation}
        ></ConservationBanner>
      </If>
      <If value={isSmall}>
        <Stack
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <PluginProductGallery
            images={props.images}
            index={index}
            setIndex={setIndex}
          ></PluginProductGallery>
        </Stack>
        <Divider
          sx={{ mt: 1 }}
          flexItem
          color='black'
          role='presentation'
        ></Divider>
        <PluginProductName title={props.title}></PluginProductName>
        <PluginPrice variant={variant} {...props} />
        <Stack width='100%' ml={1} mr={1}>
          <ActionButton
            productType={props.productType}
            learnMore={props.learnMore}
            id={props.variants.nodes[0].id}
            available={(props.variants.nodes[0] as any).availableForSale}
            name={props.title}
            isLegacy={props.tags.includes('Legacy')}
          />
          <If value={manual}>
            <Button
              variant='outlined'
              style={{
                borderRadius: '0px',
                width: '98%',
                fontFamily: 'OCR A Std',
              }}
              sx={{ mt: 2, mb: 2 }}
              color='info'
              href={props.manual_link}
            >
              Product Manual
            </Button>
          </If>
          <If value={bundle}>
            <BundleAndSave />
          </If>
          <Stack
            sx={{ justifyContent: 'center' }}
            direction={{ xs: 'column', sm: 'column', md: 'column', lg: 'row' }}
          >
            <PluginProductDesc
              variant={variant}
              setVariant={setVariant}
              {...props}
            ></PluginProductDesc>

            <Box mt={3} width='98%'>
              <ProductAccordion {...props}></ProductAccordion>
            </Box>
          </Stack>
        </Stack>

        <Divider
          sx={{ mt: 1 }}
          flexItem
          color='black'
          role='presentation'
        ></Divider>
        <If value={props.videos}>
          <VideoGallery {...props} />
        </If>
        <If value={props.sounds}>
          <Divider
            sx={{ mt: 1 }}
            flexItem
            color='black'
            role='presentation'
          ></Divider>
          <AudioPlayers files={props.sounds}></AudioPlayers>
        </If>
        <If value={alsoLike.length !== 0}>
          <SectionHeader
            title='you might also like'
            buttonText='View All'
            href='/collections/all'
          ></SectionHeader>
          <SimpleCollection products={alsoLike} />
        </If>
        <If value={props.quotes}>
          <QuoteCarousel items={props.quotes}></QuoteCarousel>
        </If>
        <ConservationBanner
          conservation={props.conservation}
        ></ConservationBanner>
      </If>
    </>
  );
}
